import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { InputField, InputType, ProviderName } from '../../../account/models/login-response.model';
import { config } from '../../../shared/config';
import { ApiService } from '../../../shared/services/core/api.service';
import { LoggingService } from '../../../shared/services/core/logging.service';
import { AuthService } from '../../../user/services/auth.service';
import { SportsAllianceSettings } from './models/sports-alliance-settings.model';

@Injectable({
  providedIn: 'root',
})
export class SportsAllianceAuthService {
  /**
   * This holds all the sports alliance settings required for sso,
   * this information will be read from the config js file
   * and also passed or defaulted from the login process.
   */
  public settings: SportsAllianceSettings;
  constructor(
    private authService: AuthService,
    private logService: LoggingService,
    private api: ApiService,
    private cookieService: CookieService
  ) {
    if (this.settings === undefined) {
      let sportsAllianceConfig = config.providerSettings.find((x) => x.providerId === ProviderName.SportsAlliance);
      if (sportsAllianceConfig != undefined && sportsAllianceConfig.settings != undefined) {
        this.settings = sportsAllianceConfig.settings as SportsAllianceSettings;
      }
    }
    if (this.settings.tenantId == undefined) {
      this.logService.warning('tenant Id is currently not in the config file');
    }
  }

  /**
   * Attempt to login using sports alliance using redirect process.
   * This will call an endpoint to generate the login URL,
   * depending on the parameters passed.
   * This can also be used for registration, handled by the parameters passed.
   * @param returnVisitPath path for previous page button within SA login page,
   * e.g. /login (which is the default value)
   * @param successredirectPath redirect path after successfully authenticated
   * e.g. /sa-callback (which is the default value)
   * @param mandatoryReg Enforces mandatory registration mode in which
   * users must register a full account rather than a lite or social account.
   * @param ssoMode ssoMode for URL, 0 = login, 1 = registration,
   * 2 = external login
   * @param clubId clubId to be passed to endpoint,
   * default to 1.
   */
  login(
    returnVisitPath: string = '/login',
    successredirectPath: string = '/sa-callback',
    mandatoryReg: boolean = false,
    ssoMode: string = '0',
    clubId: number = 1
  ) {
    this.settings.returnVisitUrl = config.portalUrl + returnVisitPath;
    this.settings.successDirectUrl = config.portalUrl + successredirectPath;
    this.settings.mandatoryReg = mandatoryReg;
    this.settings.ssoMode = ssoMode;

    const loginUrlParameters: InputField[] = this.generateLoginParameters();

    return this.api.post(`sso/JWT/GenerateUrl/${ProviderName.SportsAlliance}/${clubId}`, loginUrlParameters).pipe(
      tap((generatedUrl: any) => {
        window.location.href = generatedUrl;
      })
    );
  }

  /**
   * This method will generate the login
   * URL for sports alliance from the defined
   * settings, when login has been attempted.
   *
   * @returns array of parameters required
   * to generate the Sports Alliance
   * login URL of type InputField.
   */
  generateLoginParameters() {
    let generateLoginParams: InputField[] = [];

    if (this.settings.returnVisitUrl) {
      let returnVisitParam: InputField = {
        FieldName: 'ReturnVisitorUrl',
        InputType: InputType.Text,
        Active: true,
        Value: this.settings.returnVisitUrl,
        ErrorMessage: '',
      };
      generateLoginParams.push(returnVisitParam);
    }

    if (this.settings.successDirectUrl) {
      let successDirectParam: InputField = {
        FieldName: 'SuccessUrl',
        InputType: InputType.Text,
        Active: true,
        Value: this.settings.successDirectUrl,
        ErrorMessage: '',
      };
      generateLoginParams.push(successDirectParam);
    }

    if (this.settings.tenantId) {
      let tenantIdParam: InputField = {
        FieldName: 'Externaltenantid',
        InputType: InputType.Text,
        Active: true,
        Value: this.settings.tenantId,
        ErrorMessage: '',
      };
      generateLoginParams.push(tenantIdParam);
    }

    let mandatoryRegValue = 'false';
    if (this.settings.mandatoryReg) {
      mandatoryRegValue = 'true';
    }
    let manadatoryRegParam: InputField = {
      FieldName: 'Mandatory',
      InputType: InputType.Text,
      Active: true,
      Value: mandatoryRegValue,
      ErrorMessage: '',
    };
    generateLoginParams.push(manadatoryRegParam);

    if (this.settings.ssoMode) {
      let ssoModeParam: InputField = {
        FieldName: 'SSOMode',
        InputType: InputType.Text,
        Active: true,
        Value: this.settings.ssoMode,
        ErrorMessage: '',
      };
      generateLoginParams.push(ssoModeParam);
    }

    return generateLoginParams;
  }

  /**
   * This method is used to authenticate a token
   * that comes back from the Sports Alliance login page
   * and log the user in via the tokens returned
   * from the response.
   * @param jwtToken JWT Token returned from
   * Sports Alliance, to be authenticated and
   * passed to given endpoint.
   * @param clubId clubId to be passed to endpoint,
   * default to 1.
   * @returns boolean from status of response.
   */
  validateJwt(jwtToken: string, clubId: number = 1) {
    if (this.authService.hasAccessToken()) {
      return of(true);
    }

    let token = '';
    if (jwtToken != '' && jwtToken != undefined) {
      token = jwtToken;
    }

    return this.api.post(`sso/JWT/Validate/${ProviderName.SportsAlliance}/${clubId}`, `"${token}"`).pipe(
      tap((authResponse: any) => {
        if (authResponse) {
          this.authService.loginUsingToken(authResponse.tokens.AccessToken, authResponse.tokens.RefreshToken, '/');
          localStorage.setItem('SARefreshToken', authResponse.auth.ProviderRefreshToken.toString());
          localStorage.setItem('SAAccessToken', authResponse.auth.ProviderAccessToken.toString());
        }
      }),
      map((authResponse: any) => {
        if ((authResponse.status = 200)) {
          return true;
        } else {
          return false;
        }
      })
    );
  }

  /**
   * Log out of the portal and provider (Sports Alliance).
   * Passes provider tokens and provider ID
   * to logout for the logout endpoint
   * with portal provider details.
   */
  logout() {
    let providerTokens: { [name: string]: string | null } = {};
    providerTokens.providerRefreshToken = localStorage.getItem('SARefreshToken');
    providerTokens.providerAccessToken = localStorage.getItem('SAAccessToken');
    localStorage.removeItem('SARefreshToken');
    localStorage.removeItem('SAAccessToken');

    return this.authService.logout(ProviderName.SportsAlliance, providerTokens);
  }

  /**
   * This method is used to authenticate a domain
   * token used from the _sa_sso_upid cookie given
   * from Sports Alliance domain and log the user
   * using tokens returned.
   * @param domainCookie domain cookie to be
   * authenticated and passed to endpoint.
   * @param clubId clubId to be passed to endpoint,
   * default to 1.
   * @returns boolean from status of response.
   */
  loginUsingToken(domainCookie: string, clubId: number = 1) {
    let saDomainCookie = '';
    if (domainCookie != '' && domainCookie != undefined) {
      saDomainCookie = domainCookie;
    }

    return this.api.get(`sso/OAuth/${ProviderName.SportsAlliance}/${saDomainCookie}/${clubId}`).pipe(
      tap((authResponse: any) => {
        if (authResponse) {
          this.authService.loginUsingToken(authResponse.tokens.AccessToken, authResponse.tokens.RefreshToken, '/');
          localStorage.setItem('SARefreshToken', authResponse.auth.RefreshToken.toString());
          localStorage.setItem('SAAccessToken', authResponse.auth.AccessToken.toString());
        }
      }),
      map((authResponse: any) => {
        if ((authResponse.status = 200)) {
          return true;
        } else {
          return false;
        }
      })
    );
  }

  /**
   * Check if a named cookie exists.
   * @param cookieName cookie name to be checked.
   * @returns boolean value depending if cookie name
   * exists.
   */
  hasNamedCookie(cookieName: string): boolean {
    if (this.cookieService.check(cookieName)) {
      return true;
    }
    return false;
  }
}
